import {
  FC,
  JSX,
  ReactElement,
  Suspense,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { ConditionalWrapper } from 'components/Shared/ConditionalWrapper';
import { Spin } from 'components/UI/Spin';
import { fireEvent as fireEventVanilla, useEventbus } from 'hooks/useEventbus';
import { StepViewerLazyLoader } from './__partials/StepViewerLazyLoader';
import { StepViewerProgressNavigation } from './__partials/StepViewerProgressNavigation';
import * as Styled from './styles';
import { Portal } from '../Portal';
import { isMobile } from 'utils';
import { StepViewerEvents } from 'constants/app/eventbus-events';
import type { StepProps as AntStepProps } from 'antd';
import type { CSSProperties } from 'styled-components';

export type StepViewerProps = {
  width?: CSSProperties['width'];
  height?: CSSProperties['height'];
  steps: StepProps[];
  withoutWrapper?: boolean;
  header?: {
    show?: boolean;
    showStepsProgress?: false | 'text' | 'steps';
    showNavigation?: boolean;
    title?: string;
    isSticky?: boolean;
  };
  footer?: {
    navigationButtonsFullWidth?: boolean;
  };
  hideFooter?: boolean;
  hideProgressOnLastStep?: boolean;
  onFinish?: () => void;
  cacheable?: boolean;
  eventId?: string;
  className?: string;
  fixedFooterId?: string;
  fixedHeaderId?: string;
};

export type StepProps = {
  title: {
    text?: string;
    shortText?: string;
  };
  subtitle?: string;
  nextBtnText?: string;
  prevBtnText?: string;
  info?: {
    show?: boolean;
    text?: string | ReactElement;
  };
  component?: () => JSX.Element;
  render?: () => ReactElement;
  loader?: ReactElement;
  loaderSize?: 'small' | 'large' | 'default';
  handleToNextStepItself?: boolean;
};

const observer = new IntersectionObserver(
  ([entry]: IntersectionObserverEntry[]) => {
    if (!entry.isIntersecting) {
      entry.target.scrollIntoView({
        behavior: 'smooth',
        inline: 'start',
        block: 'nearest'
      });
      entry.target.addEventListener(
        'transitionend',
        () => {
          fireEventVanilla({
            key: StepViewerEvents.StepperStepFinishedScrolling
          });
        },
        { once: true }
      );
    } else {
      fireEventVanilla({
        key: StepViewerEvents.StepperStepFinishedScrolling
      });
    }
    observer.unobserve(entry.target);
    observer.disconnect();
  },
  { threshold: 1 }
);

export const StepViewer: FC<StepViewerProps> = ({
  width,
  height,
  header,
  hideFooter = false,
  steps,
  withoutWrapper = false,
  hideProgressOnLastStep = false,
  onFinish,
  cacheable = false,
  eventId,
  className,
  fixedFooterId,
  fixedHeaderId,
  footer
}) => {
  const {
    show: headerShow = true,
    showStepsProgress = 'steps',
    showNavigation = false,
    title: stepViewerTitle = '',
    isSticky = true
  } = header || {
    show: true,
    showStepsProgress: 'steps',
    showNavigation: false,
    isSticky: true
  };
  const { navigationButtonsFullWidth = false } = footer || {
    navigationButtonsFullWidth: false
  };
  const { t } = useTranslation();
  const totalSteps = steps.length;
  const [currentStep, setCurrentStep] = useState(1);
  const [cachedSteps, setCachedSteps] = useState<(ReactElement | null)[]>([]);
  const currentStepProps = steps[currentStep - 1];
  const stepsWrapperRef = useRef<HTMLDivElement | null>(null);
  const [footerMounted, setFooterMounted] = useState(hideFooter);
  const showPlainStep = hideProgressOnLastStep && currentStep === totalSteps;
  const eventbusId = eventId || useId();
  const { fireEvent } = useEventbus(
    {
      onEvent: ({ key, id, payload }) => {
        if (id !== eventbusId) return;
        switch (key) {
          case StepViewerEvents.RenderNextView:
            if (currentStep === totalSteps) return;
            stepperStepObserver(currentStep + 1);
            setCurrentStep(prev => prev + 1);
            break;
          case StepViewerEvents.RenderPreviousView:
            if (currentStep === 1) return;
            stepperStepObserver(currentStep - 1);
            setCurrentStep(prev => prev - 1);
            break;
          case StepViewerEvents.RenderStep:
            stepperStepObserver(payload);
            setCurrentStep(payload);
            break;
          case StepViewerEvents.FinishSteps:
            onFinish?.();
            break;
        }
      }
    },
    [currentStep, totalSteps, onFinish, eventbusId]
  );

  const headerSteps: AntStepProps[] = steps.map((step, index) => {
    return {
      title: step.title.shortText || (index + 1).toString(),
      ...(index > currentStep && { disabled: true })
    };
  });

  const StepView = useMemo(() => {
    if (!footerMounted) return null;
    if (currentStepProps.render) return currentStepProps.render();
    if (!currentStepProps.component) return null;
    const loaderSize = currentStepProps.loaderSize || 'default';
    const loader = currentStepProps.loader || (
      <Styled.StepViewerBodyLoaderFallback>
        <Spin type="absolute" size={loaderSize} />
      </Styled.StepViewerBodyLoaderFallback>
    );

    return (
      <Suspense
        fallback={
          <StepViewerLazyLoader eventId={eventbusId}>
            {loader}
          </StepViewerLazyLoader>
        }
      >
        <currentStepProps.component />
      </Suspense>
    );
  }, [footerMounted, currentStepProps]);

  const showHeaderStepsOrNavigation = !!showStepsProgress || showNavigation;

  const stepperStepObserver = (step: number) => {
    if (!stepsWrapperRef.current || showStepsProgress !== 'steps') return;
    const stepsElement = stepsWrapperRef.current.children[0];
    if (!stepsElement?.hasChildNodes?.()) return;
    const currentStepElement = stepsElement.children[step - 1];
    observer.observe(currentStepElement);
  };

  useEffect(() => {
    if (cacheable) {
      fireEvent({
        key: StepViewerEvents.EnableProceeding,
        payload: currentStep,
        id: eventbusId
      });
    }
  }, [currentStep, cacheable]);

  useEffect(() => {
    if (!cacheable) {
      setCachedSteps([StepView]);
      return;
    }
    setCachedSteps(prev => {
      const newRenderedViews = [...prev];
      newRenderedViews[currentStep - 1] = StepView;
      return newRenderedViews;
    });
  }, [StepView, cacheable, currentStep]);

  const renderedSteps = useMemo(() => {
    return cachedSteps.map((CachedStep, index, array) => (
      <Styled.StepViewerBodyStepWrapper
        key={index}
        style={{
          display:
            array.length === 1 || index === currentStep - 1 ? 'initial' : 'none'
        }}
      >
        {CachedStep}
      </Styled.StepViewerBodyStepWrapper>
    ));
  }, [cachedSteps, currentStep]);

  const goToStep = (step: number) => {
    if (step <= currentStep - 1) {
      fireEvent({
        key: StepViewerEvents.RenderStep,
        id: eventId,
        payload: step + 1
      });
    }
    if (step === currentStep) {
      fireEvent({
        key: StepViewerEvents.InitiateNextViewRender,
        id: eventId
      });
    }
  };

  return (
    <ConditionalWrapper
      condition={!withoutWrapper}
      wrapper={children => (
        <Styled.StepViewerWrapper>{children}</Styled.StepViewerWrapper>
      )}
    >
      <Styled.StepViewer
        $width={width}
        $height={height}
        $withoutWrapper={withoutWrapper}
        className={className}
      >
        {!showPlainStep && headerShow && (
          <ConditionalWrapper
            condition={!!fixedHeaderId}
            wrapper={children => (
              <Portal rootId={fixedHeaderId}>{children}</Portal>
            )}
          >
            <Styled.StepViewerHeader $isSticky={isSticky}>
              <Styled.StepViewerHeaderTitle>
                {currentStepProps.title.text || stepViewerTitle}
              </Styled.StepViewerHeaderTitle>
              {currentStepProps.subtitle && (
                <Styled.StepViewerHeaderSubtitle>
                  {currentStepProps.subtitle}
                </Styled.StepViewerHeaderSubtitle>
              )}
              {showHeaderStepsOrNavigation && (
                <Styled.StepsWrapper
                  $isStepsText={showStepsProgress === 'text'}
                >
                  <Styled.StepsWrapperLeft ref={stepsWrapperRef}>
                    {showStepsProgress === 'steps' && (
                      <Styled.Steps
                        current={currentStep - 1}
                        items={headerSteps}
                        size="small"
                        labelPlacement={isMobile() ? 'vertical' : 'horizontal'}
                        responsive={false}
                        onChange={goToStep}
                      />
                    )}
                    {showStepsProgress === 'text' && (
                      <Styled.StepsAsText>
                        {t('generic.stepsProgressCurrentOfTotal', {
                          current: currentStep,
                          total: totalSteps
                        })}
                      </Styled.StepsAsText>
                    )}
                  </Styled.StepsWrapperLeft>
                  {showNavigation && (
                    <StepViewerProgressNavigation
                      withoutWrapper
                      nextBtnText={currentStepProps.nextBtnText}
                      prevBtnText={currentStepProps.prevBtnText}
                      handleToNextStep={
                        !currentStepProps.handleToNextStepItself
                      }
                      eventId={eventbusId}
                      fixedFooterId={fixedFooterId}
                      blockButtons={navigationButtonsFullWidth}
                      info={{
                        totalSteps,
                        currentStep,
                        show: false
                      }}
                    />
                  )}
                </Styled.StepsWrapper>
              )}
            </Styled.StepViewerHeader>
          </ConditionalWrapper>
        )}
        <Styled.StepViewerBody>{renderedSteps}</Styled.StepViewerBody>
        {!(showPlainStep || hideFooter) && (
          <Styled.StepViewerFooter $contentFixed={!!fixedFooterId}>
            <StepViewerProgressNavigation
              ref={() => setFooterMounted(true)}
              nextBtnText={currentStepProps.nextBtnText}
              prevBtnText={currentStepProps.prevBtnText}
              handleToNextStep={!currentStepProps.handleToNextStepItself}
              eventId={eventbusId}
              fixedFooterId={fixedFooterId}
              blockButtons={navigationButtonsFullWidth}
              info={{
                currentStep,
                totalSteps,
                text: currentStepProps.info?.text,
                show: currentStepProps.info?.show
              }}
            />
          </Styled.StepViewerFooter>
        )}
      </Styled.StepViewer>
    </ConditionalWrapper>
  );
};
